import type { LayerProps, SourceProps } from 'react-map-gl'

export type MapLegend = { items: { label: string; color: string; opacity?: number; symbol?: 'arrow' }[] }
export type MapRenderLayer = Exclude<LayerProps, { type: 'custom' }>
export type MapLayerSource = Extract<SourceProps, { type: 'raster' | 'vector' }> & {
  id: string
  asset_id?: string
  layers: MapRenderLayer[]
}
export type MapLayerVariant = {
  id: string
  values: Record<string, string>
  period?: string
  percentile?: number
  render: { sources: MapLayerSource[] }
}
export type MapLayerDataset = {
  id: string
  category?: 'general'
  hazard_code?: string
  label: string
  tags: string[]
  exclusive_group?: string
  bounds: [number, number, number, number]
  description?: string
  selectors: { key: string; label: string; labels?: Record<string, string> }[]
  variables: Record<string, { label: string; units?: string; legend: MapLegend }>
  variants: MapLayerVariant[]
}
export type MapLayerCatalogue = {
  schema_version: 2
  hazards: { code: string; label: string }[]
  datasets: MapLayerDataset[]
}

export const MAP_LAYER_CATALOGUE_KEY = 'map-layer-catalogue'

export async function loadMapLayerCatalogue(): Promise<MapLayerCatalogue> {
  const response = await fetch(`${process.env.PUBLIC_URL || ''}/map-layers.json`)
  if (!response.ok) throw new Error('Unable to load map layers')
  const catalogue = await response.json()
  if (catalogue.schema_version !== 2 || !Array.isArray(catalogue.datasets))
    throw new Error('Unsupported map layer catalogue')
  return catalogue
}

export function selectorOptions(dataset: MapLayerDataset, variant: MapLayerVariant, key: string) {
  const preceding = dataset.selectors.slice(
    0,
    dataset.selectors.findIndex(selector => selector.key === key),
  )
  return [
    ...new Set(
      dataset.variants
        .filter(candidate =>
          preceding.every(selector => candidate.values[selector.key] === variant.values[selector.key]),
        )
        .map(candidate => candidate.values[key])
        .filter(value => value != null),
    ),
  ]
}

export function changeVariant(dataset: MapLayerDataset, current: MapLayerVariant, key: string, value: string) {
  return (
    dataset.variants
      .filter(candidate => candidate.values[key] === value)
      .sort((a, b) => {
        const score = (candidate: MapLayerVariant) =>
          Object.entries(current.values).filter(
            ([field, selected]) => field !== key && candidate.values[field] === selected,
          ).length
        return score(b) - score(a)
      })[0] || current
  )
}

export function variantCaption(dataset: MapLayerDataset, variant: MapLayerVariant) {
  return [
    dataset.variables[variant.values.layer].label,
    ...dataset.selectors
      .filter(selector => selector.key !== 'layer')
      .map(selector => {
        const value = variant.values[selector.key]
        return value == null ? null : selector.labels?.[value] || value
      }),
    variant.period,
    variant.percentile == null ? null : `P${variant.percentile}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

export function layerPaint(layer: MapRenderLayer, opacity: number) {
  const key = {
    raster: 'raster-opacity',
    fill: 'fill-opacity',
    line: 'line-opacity',
    symbol: 'icon-opacity',
    circle: 'circle-opacity',
  }[layer.type]
  const paint = layer.paint || {}
  if (!key) return paint
  const base = paint[key] ?? 1
  return { ...paint, [key]: typeof base === 'number' ? base * opacity : ['*', base, opacity] }
}

export const climateLayersEnabled = (user: any) => user?.organisation?.features?.projections === true

export const datasetAvailable = (dataset: MapLayerDataset, climateEnabled: boolean) =>
  dataset.category === 'general' || climateEnabled

export function toggleDataset(
  datasets: MapLayerDataset[],
  selected: Record<string, string>,
  dataset: MapLayerDataset,
  enabled: boolean,
) {
  const next = { ...selected }
  if (enabled) {
    if (dataset.exclusive_group)
      for (const other of datasets) {
        if (other.exclusive_group === dataset.exclusive_group) delete next[other.id]
      }
    next[dataset.id] = dataset.variants[0].id
  } else delete next[dataset.id]
  return next
}
